import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import SvgLoadingAnimation from "../../../components/loading";
import HelpHint from "../../../components/help-hint/HelpHint";
import {
  aggregateBy,
  filterActivitiesByCompletion,
  filterActivitiesByPeriod,
  sumActivityHours,
  NO_COLUMN_LABEL,
  type ActivityEntry,
} from "../../projects/components/TaskBoard/taskBoardActivity";
import { useActivityEntries } from "../../projects/components/TaskBoard/useActivityEntries";
import type {
  TaskCompletionFilter,
  TaskPeriodFilter,
} from "../../projects/components/TaskBoard/taskBoardUtils";
import ActivityBarChart, {
  type ActivityProjectBand,
} from "./ActivityBarChart";
import {
  buildActivityColorScale,
  formatHours,
  COLUMN_PALETTE,
  STAFF_PALETTE,
} from "./activityChartStyle";
import {
  projectBannerBackground,
  projectBannerTextColor,
} from "./projectBannerColor";

/**
 * アクティビティ集計ビュー（横断タスクボード `/taskboard?view=activity`）。
 *
 * タスクボードのセル本文に書かれた `@山田[3h]` を全プロジェクト横断で集計する。
 * 元データは `GET /api/task-boards/activity-source` の 1 リクエストで、
 * 参加中の**全プロジェクト**（進行中・完了で絞らない）が対象。BE は作業時間つき
 * メンションを含むタスクへ粗く絞るだけで、担当者・時間・日付の解釈はここまで
 * 通してきたセル本文の記法から FE が行う。
 *
 * 期間（日付チップ + 月次）・完了（行単位: チェック済み = 実績 / 未チェック =
 * 予定）・検索・担当者チップ・プロジェクトチップは親（CrossProjectTaskBoard）の
 * 共通フィルターを受け取る。
 *
 * 画面はタイトルもセクション分割もカード囲みも持たず、プロジェクトごとの
 * 3 段の帯（プロジェクト帯 / 項目ごと / ユーザーごと）を時間の多い順に
 * 全幅へそのまま積むだけ（表示が 1 まとまりでスクロールも無いため、囲いは
 * 情報を足さない）。幅は最大プロジェクト基準の時間比で、全体に占める割合は
 * ラベルの % で読む。見た目・配色はタスクボード（テーブル）と揃える
 * （詳細は ActivityBarChart のコメントを参照）。
 */

const Section = styled.section`
  width: 100%;
  padding: 16px;
  border: 1px solid var(--soremo-border);
  border-radius: 8px;
  background: #fff;
`;

/** 更新日で暫定集計した行。クリックでそのプロジェクトのタスクボードを開く */
const EstimatedRow = styled.button`
  display: grid;
  grid-template-columns: minmax(80px, 160px) 1fr auto;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--soremo-light-background, #f5f5f5);
  }

  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HELP_DESCRIPTION = `タスクボードのセルに「@山田[3h]」のように書くと、その担当者の作業時間として集計されます。
[1.5h]（小数）・[30m]（分）も使えます。単位を省いた [3] は時間として扱います。
何月分になるかは、同じ行に書かれた日付で決まります（行に日付がなければセル、次にタスク行の日付を使います）。
日付がどこにも書かれていない場合は、その行の更新日で暫定的に集計します。`;

/** 更新日で暫定集計した行の一覧に出す最大件数（多すぎると画面が埋まるため） */
const ESTIMATED_LIST_LIMIT = 20;

interface ActivityMonitorViewProps {
  /** 共通の期間フィルター（日付チップ + 月次）。null = すべて */
  period: TaskPeriodFilter | null;
  /** 共通の完了フィルター（行単位: チェック済み = 実績 / 未チェック = 予定） */
  completionFilter?: TaskCompletionFilter;
  /** 検索文字列（コードネーム・タスク名の部分一致）。空 = 絞り込みなし */
  searchQuery?: string;
  /** 担当者チップ（@ 表示名）。空 = 絞り込みなし */
  selectedMentions?: string[];
  /** プロジェクトチップ（project_id）。空 = 全プロジェクト */
  projectIds?: string[];
}

const EMPTY_MENTIONS: string[] = [];
const EMPTY_PROJECT_IDS: string[] = [];

const ActivityMonitorView: React.FC<ActivityMonitorViewProps> = ({
  period,
  completionFilter = "all",
  searchQuery = "",
  selectedMentions = EMPTY_MENTIONS,
  projectIds = EMPTY_PROJECT_IDS,
}) => {
  const navigate = useNavigate();

  const { entries, projects, isLoading, error } = useActivityEntries();

  // 配色は「全期間のデータ」から作る。期間を切り替えても同じ工程・同じ担当者が
  // 同じ色であってほしいため（絞り込み後から作ると色が動いてしまう）。
  const columnColorOf = useMemo(() => {
    const columns = new Set<string>();
    for (const e of entries) columns.add(e.columnTitle || NO_COLUMN_LABEL);
    return buildActivityColorScale([...columns].sort(), COLUMN_PALETTE);
  }, [entries]);

  const staffColorOf = useMemo(() => {
    const staff = new Set<string>();
    for (const e of entries) staff.add(e.name);
    return buildActivityColorScale([...staff].sort(), STAFF_PALETTE);
  }, [entries]);

  // プロジェクトの帯はバナーカラーで塗り、文字色もバナーに合わせる
  //（テーブルのカードヘッダーと同じ見た目にする）
  const projectStyleOf = useMemo(() => {
    const byId = new Map(
      projects.map((p) => [
        p.project_id,
        {
          background: projectBannerBackground(p.banner_color),
          textColor: projectBannerTextColor(p.banner_color),
        },
      ]),
    );
    const fallback = {
      background: projectBannerBackground(null),
      textColor: projectBannerTextColor(null),
    };
    return (id: string) => byId.get(id) ?? fallback;
  }, [projects]);

  // 検索（コードネーム・タスク名の部分一致・大文字小文字無視）で集計対象を絞る
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const searched = useMemo<ActivityEntry[]>(() => {
    if (!normalizedQuery) return entries;
    return entries.filter(
      (e) =>
        e.projectName.toLowerCase().includes(normalizedQuery) ||
        (e.taskTitle || "").toLowerCase().includes(normalizedQuery),
    );
  }, [entries, normalizedQuery]);

  // 期間 → 完了 → 担当者チップ → プロジェクトチップの順で絞り込む
  //（順序は結果に影響しないが、読みやすさのため共通フィルターの行順に揃える）。
  const scoped = useMemo(() => {
    let result = filterActivitiesByPeriod(searched, period);
    result = filterActivitiesByCompletion(result, completionFilter);
    if (selectedMentions.length > 0) {
      result = result.filter((e) => selectedMentions.includes(e.name));
    }
    if (projectIds.length > 0) {
      result = result.filter((e) => projectIds.includes(e.projectId));
    }
    return result;
  }, [searched, period, completionFilter, selectedMentions, projectIds]);

  // プロジェクトごとの帯データ（プロジェクト帯 + 項目ごと + ユーザーごと）。
  // 並びは合計時間の降順（aggregateBy の既定）。
  const projectBands = useMemo<ActivityProjectBand[]>(() => {
    const byColumn = aggregateBy(scoped, "project", "column");
    const staffByKey = new Map(
      aggregateBy(scoped, "project", "staff").map((b) => [b.key, b.segments]),
    );
    return byColumn.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      hours: bucket.hours,
      ...projectStyleOf(bucket.key),
      columnSegments: bucket.segments,
      staffSegments: staffByKey.get(bucket.key) ?? [],
    }));
  }, [scoped, projectStyleOf]);

  const totalHours = sumActivityHours(scoped);

  // 日付がどこにも書かれておらず、行の更新日で暫定集計したもの。
  // 正しい月へ寄せてもらうため、タスク単位にまとめて追記を促す。
  const estimatedTasks = useMemo(() => {
    const byTask = new Map<
      string,
      {
        taskId: string;
        projectId: string;
        projectName: string;
        taskTitle: string;
        hours: number;
      }
    >();
    for (const entry of scoped) {
      if (!entry.dateEstimated) continue;
      const existing = byTask.get(entry.taskId);
      if (existing) {
        existing.hours += entry.hours;
        continue;
      }
      byTask.set(entry.taskId, {
        taskId: entry.taskId,
        projectId: entry.projectId,
        projectName: entry.projectName,
        taskTitle: entry.taskTitle || "無題のタスク",
        hours: entry.hours,
      });
    }
    return [...byTask.values()].sort((a, b) => b.hours - a.hours);
  }, [scoped]);

  if (isLoading) {
    return (
      <div className="u-col-center u-w100 u-mt16">
        <SvgLoadingAnimation />
      </div>
    );
  }

  if (error) {
    return <div>エラーが発生しました</div>;
  }

  return (
    <div className="u-col u-gap16 u-w100">
      {/* 集計グラフはカードで囲わず全幅に広げる（囲いは情報を足さないため） */}
      <div className="u-col u-w100">
        <div className="u-row u-items-center u-gap8 u-mb8">
          <h2 className="heading-14">合計 {formatHours(totalHours)}</h2>
          <div style={{ marginLeft: "auto" }}>
            <HelpHint description={HELP_DESCRIPTION} ariaLabel="記法のヘルプ" />
          </div>
        </div>

        {projectBands.length === 0 ? (
          <p className="bodytext-13 u-text-light-gray">
            この期間の工数はありません
          </p>
        ) : (
          <ActivityBarChart
            projects={projectBands}
            columnColorOf={columnColorOf}
            staffColorOf={staffColorOf}
          />
        )}
      </div>

      {/* 日付が書かれていない行は集計から落とさず、更新日で暫定的に数えている。
          正しい月へ寄せられるよう、対象のタスクを挙げて追記を促す。 */}
      {estimatedTasks.length > 0 && (
        <Section>
          <div className="u-row u-items-center u-gap8 u-mb8">
            <h2 className="heading-14">日付の追記をおすすめします</h2>
            <span className="bodytext-13 u-text-light-gray">
              {estimatedTasks.length}件
            </span>
          </div>
          <p className="bodytext-13 u-text-light-gray u-mb8">
            日付が書かれていないため、行の更新日で暫定的に集計しています。
            セルに「6/20」のように日付を書くと、正しい月へ計上されます。
          </p>
          <div className="u-col u-gap4">
            {estimatedTasks.slice(0, ESTIMATED_LIST_LIMIT).map((task) => (
              <EstimatedRow
                key={task.taskId}
                type="button"
                onClick={() => navigate(`/taskboard?project=${task.projectId}`)}
              >
                <span className="bodytext-13 u-text-light-gray">
                  {task.projectName}
                </span>
                <span className="bodytext-13">{task.taskTitle}</span>
                <span className="bodytext-13 u-text-light-gray">
                  {formatHours(task.hours)}
                </span>
              </EstimatedRow>
            ))}
            {estimatedTasks.length > ESTIMATED_LIST_LIMIT && (
              <span className="bodytext-13 u-text-light-gray">
                ほか {estimatedTasks.length - ESTIMATED_LIST_LIMIT} 件
              </span>
            )}
          </div>
        </Section>
      )}
    </div>
  );
};

export default ActivityMonitorView;
